export default function EconomicRevitalization() {
  const impacts = [
    {
      icon: 'ri-shopping-cart-line',
      title: '地域消費の拡大',
      description: '従業員による日常的な消費活動が地域経済を活性化',
      examples: ['食料品・日用品の購入', '飲食店の利用', 'サービス業の利用']
    },
    {
      icon: 'ri-store-line',
      title: '取引先の増加',
      description: '養殖事業に必要な資材や設備の調達で地域企業と連携',
      examples: ['飼料・資材の調達', '設備メンテナンス', '物流サービス']
    },
    {
      icon: 'ri-restaurant-line',
      title: '販路の創出',
      description: '地域の飲食店や小売店への製品供給で新しい商流を創出',
      examples: ['地元飲食店への供給', '直売所での販売', '地域ブランド化']
    },
    {
      icon: 'ri-user-add-line',
      title: '人口増加効果',
      description: '従業員とその家族の移住により地域人口が増加',
      examples: ['若年層の流入', '子育て世代の増加', '地域活力の向上']
    }
  ];

  return (
    <section className="py-24 px-6 bg-gradient-to-b from-white to-[#F5F9F6]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-[#C8E6C9] mb-6">
            <i className="ri-line-chart-line text-[#2D7A3E] text-lg mr-2"></i>
            <span className="text-[#2D7A3E] text-sm font-semibold whitespace-nowrap">地域経済の活性化</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1A2332] mb-6">
            地域全体に広がる<br />
            経済効果
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            養殖事業の立ち上げは、雇用創出だけでなく、地域経済全体に波及効果をもたらします。<br />
            新しい産業が地域に根付くことで、持続可能な経済循環を生み出します。
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {impacts.map((impact, index) => (
            <div
              key={index}
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-[#E8F5E9] text-center"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-[#2D7A3E] to-[#4CAF50] rounded-xl flex items-center justify-center mb-6 mx-auto">
                <i className={`${impact.icon} text-white text-3xl`}></i>
              </div>
              <h3 className="text-2xl font-bold text-[#1A2332] mb-3">{impact.title}</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">{impact.description}</p>
              <div className="space-y-2 inline-flex flex-col items-start">
                <p className="text-sm font-semibold text-[#2D7A3E] mb-3">具体例</p>
                {impact.examples.map((example, idx) => (
                  <div key={idx} className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-[#2D7A3E] rounded-full mr-3"></div>
                    <span className="text-sm text-gray-700">{example}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white p-10 rounded-2xl shadow-xl border border-[#E8F5E9] mb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/9c43bd4604eb11aa2ab9f836e2d0a840.jpg"
                  alt="活性化する地域経済"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </div>

            <div>
              <h3 className="text-3xl font-bold text-[#1A2332] mb-6">
                持続可能な<br />
                経済循環の創出
              </h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                養殖事業を核として、地域内での経済循環を強化。地域の企業や商店と連携し、お金が地域内で循環する仕組みを構築します。
              </p>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-[#2D7A3E] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-refresh-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1A2332] mb-2">地域内経済循環</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      従業員の給与が地域での消費に回り、地域企業の売上増加につながります。この循環が地域経済全体を底上げします。
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-[#2D7A3E] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-links-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1A2332] mb-2">地域企業との連携</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      資材調達、設備メンテナンス、物流など、可能な限り地域企業と取引することで、地域経済への貢献を最大化します。
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-[#2D7A3E] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-seedling-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1A2332] mb-2">新産業の創出</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      養殖事業を起点に、加工業、観光業など関連産業の創出も視野に入れ、地域の産業構造を多様化します。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}